import { Fragment, MouseEventHandler } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  PencilSquareIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  SparklesIcon,
  NoSymbolIcon,
  MegaphoneIcon,
  // NoSymbolIcon,
} from "@heroicons/react/20/solid";
import { isObjectEmpty, isReleaseDraft, isReleaseOnSale, isReleaseScheduled } from "utils/helpers";
import { useDataProviderContext } from "pages/MagnetDetails/DataProvider";

export default function ReleaseTableDropDownMenu({
  releaseData: release,
  setOpenEndReleaseModal,
  setOpenDeleteDraftReleaseModal,
  setOpenConfirmPublishReleaseModal,
  onClick,
  setIsManageMagnetReleaseModalOpen,
  onEditReleaseButtonClick,
  setOpenPrivateReleaseModal,
  setOpenExclusiveReleaseModal,
}: {
  releaseData: Release;
  onClick: MouseEventHandler;
  setIsManageMagnetReleaseModalOpen: (b: boolean) => void;
  onEditReleaseButtonClick: (id: number) => void;
  setOpenEndReleaseModal: (openEndReleaseModal: boolean) => void;
  setOpenConfirmPublishReleaseModal: (openConfirmPublishReleaseModal: boolean) => void;
  setOpenDeleteDraftReleaseModal: (openDeleteDraftReleaseModal: boolean) => void;
  setOpenPrivateReleaseModal: (openPrivateReleaseModal: boolean) => void;
  setOpenExclusiveReleaseModal: (openExclusiveReleaseModal: boolean) => void;
}) {
  const { magnetDetailsState } = useDataProviderContext();
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="flex w-full items-center rounded-full text-white hover:opacity-70" onClick={onClick}>
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon className="h-8 w-8" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-10 scale-50"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-10 scale-50"
      >
        <Menu.Items className="absolute right-0 z-10 w-max origin-top-right rounded-md bg-[#1f2732] shadow-[rgba(0,0,0,0.5)_3px_3px_8px,_rgba(0,0,0,0.3)_6px_6px_28px] ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="pb-1 pt-1 font-satoshi font-medium">
            <Menu.Item>
              <button
                type="button"
                className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                onClick={() => setIsManageMagnetReleaseModalOpen(true)}
              >
                <InformationCircleIcon className="mr-3 h-4 w-4 opacity-95" aria-hidden="true" />
                Release Details
              </button>
            </Menu.Item>
            {isReleaseDraft(release.status) && (
              <>
                <Menu.Item>
                  <button
                    type="button"
                    className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                    onClick={() => setOpenConfirmPublishReleaseModal(true)}
                  >
                    <MegaphoneIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                    Publish Release
                  </button>
                </Menu.Item>
                <Menu.Item>
                  <button
                    type="button"
                    className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                    onClick={() => onEditReleaseButtonClick(release.id)}
                  >
                    <PencilSquareIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                    Edit Release
                  </button>
                </Menu.Item>
                <Menu.Item>
                  <button
                    type="button"
                    className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                    onClick={() => setOpenPrivateReleaseModal(true)}
                  >
                    <EyeSlashIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                    {isObjectEmpty(release.event) ? "Make Release Private" : "Stop Private Release"}
                  </button>
                </Menu.Item>
                {magnetDetailsState?.type === 0 && (
                  <Menu.Item>
                    <button
                      type="button"
                      className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                      onClick={() => setOpenExclusiveReleaseModal(true)}
                    >
                      <SparklesIcon className="mr-3 h-4 w-4 text-white" aria-hidden="true" />
                      {release.is_whitelisted ? "Exclusive Release Options" : "Make Release Exclusive"}
                    </button>
                  </Menu.Item>
                )}
                <Menu.Item>
                  <button
                    type="button"
                    className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                    onClick={() => setOpenDeleteDraftReleaseModal(true)}
                  >
                    <TrashIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                    Delete Draft Release
                  </button>
                </Menu.Item>
              </>
            )}
            {(isReleaseScheduled(release.status) || isReleaseOnSale(release.status)) && (
              <Menu.Item>
                <button
                  type="button"
                  className="m-0 flex w-full items-center py-1.5 pl-4 pr-7 text-xs text-white hover:bg-[#383f49]"
                  onClick={() => {
                    setOpenEndReleaseModal(true);
                  }}
                >
                  <NoSymbolIcon className="mr-3 h-4 w-4" aria-hidden="true" />
                  End Release
                </button>
              </Menu.Item>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
